import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../../software/api";
import { toast } from "sonner";
import { X, ChevronLeft, ChevronRight, Droplet, FileText } from "lucide-react";
import { ItEmailSetup } from "./ItEmailSetup";
import DocumentScripts from "./DocumentScripts";

// Read-only replica of the MS Access Forms_CompanyDetails; SCRIPTS opens the client document wording editor.
function Field({ label, value, wide, multiline }) {
  return (
    <div className={`flex items-start gap-3 ${wide ? "col-span-2" : ""}`}>
      <div className="w-28 shrink-0 text-right text-[12px] font-semibold text-slate-700 pt-1.5">{label}:</div>
      <div
        data-testid={`company-field-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        className={`flex-1 rounded-sm border border-slate-300 bg-white px-2 py-1 text-[13px] text-slate-900 ${multiline ? "min-h-[3.25rem] whitespace-pre-wrap leading-snug" : "truncate"}`}
      >
        {value || <span className="text-slate-300">—</span>}
      </div>
    </div>
  );
}

export default function CompanyDetails({ onClose }) {
  const [rows, setRows] = useState([]);
  const [idx, setIdx] = useState(0);
  const [scripts, setScripts] = useState(false);
  const [itSetup, setItSetup] = useState(false);

  useEffect(() => {
    api("GET", "/company-setup")
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setRows(list);
        const picked = (localStorage.getItem("aqualux_company") || "").trim();
        const found = list.findIndex((r) => (r.name || "").trim() === picked);
        setIdx(found >= 0 ? found : 0);
      })
      .catch(() => toast.error("Could not load company details"));
  }, []);

  const c = useMemo(() => rows[idx] || {}, [rows, idx]);
  const canPage = rows.length > 1;

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/55 backdrop-blur-sm flex items-start justify-center p-4 sm:p-8 overflow-y-auto" data-testid="overlay-company-details">
      {scripts && <DocumentScripts onClose={() => setScripts(false)} />}
      {itSetup && <ItEmailSetup onClose={() => setItSetup(false)} />}
      <div className="relative w-full max-w-3xl my-2 rounded-lg overflow-hidden shadow-2xl border border-sky-300 bg-gradient-to-b from-sky-100 to-sky-200">
        {/* title bar */}
        <div className="flex items-center justify-between bg-[#2a9fd6] text-white px-3 py-1.5">
          <div className="flex items-center gap-2 text-[13px] font-semibold"><FileText className="h-4 w-4" /> Company Details</div>
          <button onClick={onClose} data-testid="company-details-close-x" title="Close" className="h-6 w-6 rounded-sm hover:bg-white/20 flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-5">
          {/* header row */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-white shadow flex items-center justify-center"><Droplet className="h-6 w-6 text-sky-500" /></div>
              <h2 className="text-2xl font-display font-bold text-slate-700">Company Details</h2>
              <span className="ml-1 text-[11px] font-semibold uppercase tracking-wider text-sky-700 bg-white/70 rounded-full px-2 py-0.5">Read Only</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setItSetup(true)} data-testid="company-it-email-btn"
                className="rounded-sm bg-[#123a63] px-3 py-1.5 text-[12px] font-bold text-white hover:bg-[#1a4f8a]">EMAIL SETUP FOR IT</button>
              <button onClick={() => setItSetup(true)} data-testid="company-it-email-btn"
                className="rounded-sm bg-[#123a63] px-3 py-1.5 text-[12px] font-bold text-white hover:bg-[#1a4f8a]">EMAIL SETUP FOR IT</button>
              <button onClick={() => setScripts(true)} data-testid="company-scripts-btn"
                className="rounded-sm bg-[#2a9fd6] px-3 py-1.5 text-[12px] font-bold text-white hover:bg-[#1f8cc0]">SCRIPTS</button>
              <button disabled={!canPage} onClick={() => setIdx((i) => (i - 1 + rows.length) % rows.length)} data-testid="company-view-previous"
                className="inline-flex items-center gap-1 rounded-sm border border-slate-400 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"><ChevronLeft className="h-4 w-4" /> View Previous</button>
              <button disabled={!canPage} onClick={() => setIdx((i) => (i + 1) % rows.length)} data-testid="company-view-next"
                className="inline-flex items-center gap-1 rounded-sm border border-slate-400 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40">View Next <ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>

          <div className="flex gap-5">
            {/* fields column */}
            <div className="flex-1 grid grid-cols-2 gap-x-4 gap-y-2">
              <Field label="Company Name" value={c.name} wide />
              <Field label="ACN" value={c.acn} />
              <Field label="ABN" value={c.abn} />
              <Field label="Address" value={c.address} wide />
              <Field label="Suburb" value={c.suburb} />
              <div className="flex items-start gap-3">
                <Field label="State" value={c.state} />
                <Field label="Post Code" value={c.postcode} />
              </div>
              <Field label="Phone" value={c.phone} wide />
              <Field label="Fax" value={c.fax} wide />
              <Field label="Email" value={c.email} wide />
              <Field label="Website" value={c.website} wide />
              <Field label="Bank" value={c.bank} wide />
              <Field label="Branch" value={c.branch} wide />
              <Field label="Account Name" value={c.account_name} wide />
              <Field label="BSB" value={c.bsb} />
              <Field label="Account" value={c.account} />
              <Field label="Trading Hours" value={c.trading_hours} wide multiline />
              <Field label="Email Footer" value={c.email_footer} wide multiline />
            </div>
          </div>

          {/* logo */}
          <div className="flex items-start gap-3 mt-4">
            <div className="w-28 shrink-0 text-right text-[12px] font-semibold text-slate-700 pt-1.5">Logo:</div>
            <div className="flex-1 rounded-sm border border-slate-300 bg-white p-3 flex items-center justify-center min-h-[6rem]">
              {c.logo_url ? <img src={c.logo_url} alt={c.name} className="max-h-24 w-auto object-contain" data-testid="company-logo" /> : <span className="text-slate-300 text-sm">No logo</span>}
            </div>
          </div>

          <button onClick={onClose} data-testid="company-details-close" className="mt-5 w-full rounded-md bg-slate-900 hover:bg-slate-700 text-white font-bold py-2.5">Close</button>
        </div>
      </div>
    </div>
  );
}
